/**
 * Rise Gift Card Tagger
 * 
 * After Rise.ai completes its cart mutations (adding email/message properties),
 * this script tags the final Rise gift card line(s) with `_isgiftcard: 'true'`
 * so they can be identified in checkout, order processing, and analytics.
 */

import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * @typedef {Object} CartLineItem
 * @property {string} key - Unique line item key used by /cart/change.js
 * @property {Record<string, string>} [properties]
 * @property {number} quantity
 * @property {string} [vendor]
 * @property {string} [product_type]
 */

/**
 * @typedef {Object} Cart
 * @property {CartLineItem[]} [items]
 * @property {number} [item_count]
 */

/**
 * Fetch the current cart as JSON
 * @returns {Promise<Cart>}
 */
async function fetchCartJson() {
  const res = await fetch(`${Theme.routes.cart_url}.js`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });

  if (!res.ok) {
    throw new Error('Failed to fetch cart.js');
  }
  return res.json();
}

/**
 * Tag a specific cart line with _isgiftcard property.
 * Uses the line item key for an in-place property update that doesn't
 * remove/re-add the line (avoids the visual flash in the cart drawer).
 * @param {CartLineItem} item - Cart line item
 * @returns {Promise<boolean>} - Whether the line was tagged
 */
async function tagLineAsGiftCard(item) {
  const existingProps = item.properties || {};

  if (existingProps._isgiftcard === 'true' || existingProps.__isgiftcard === 'true') {
    return false;
  }

  const newProps = {
    ...existingProps,
    _isgiftcard: 'true',
  };

  const res = await fetch(`${Theme.routes.cart_change_url}.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      id: item.key,
      quantity: item.quantity,
      properties: newProps,
    }),
  });

  if (!res.ok) {
    return false;
  }
  
  return true;
}

/** Track whether a tagging operation is in progress */
let isTagging = false;
let isTaggingScheduled = false;

let isCheckoutLocked = false;

const CHECKOUT_BUTTON_SELECTOR = '[data-checkout-button], [data-express-checkout-button]';

/**
 * @returns {HTMLButtonElement[]}
 */
function getCheckoutButtons() {
  return Array.from(document.querySelectorAll(CHECKOUT_BUTTON_SELECTOR)).filter(
    (button) => button instanceof HTMLButtonElement,
  );
}

function syncCheckoutButtons() {
  const checkoutButtons = getCheckoutButtons();

  for (const checkoutButton of checkoutButtons) {
    if (isCheckoutLocked) {
      if (checkoutButton.dataset.riseGiftCardLock !== 'true') {
        checkoutButton.dataset.riseGiftCardWasDisabled = checkoutButton.disabled ? 'true' : 'false';
      }

      checkoutButton.dataset.riseGiftCardLock = 'true';
      checkoutButton.disabled = true;
      checkoutButton.setAttribute('aria-disabled', 'true');
      continue;
    }

    if (checkoutButton.dataset.riseGiftCardLock !== 'true') continue;

    const wasDisabled = checkoutButton.dataset.riseGiftCardWasDisabled === 'true';
    checkoutButton.disabled = wasDisabled;

    if (wasDisabled) {
      checkoutButton.setAttribute('aria-disabled', 'true');
    } else {
      checkoutButton.removeAttribute('aria-disabled');
    }

    delete checkoutButton.dataset.riseGiftCardLock;
    delete checkoutButton.dataset.riseGiftCardWasDisabled;
  }
}

/**
 * @param {boolean} disabled
 */
function setCheckoutDisabled(disabled) {
  isCheckoutLocked = disabled;
  syncCheckoutButtons();
}

function resetAddWindowState() {
  addCount = 0;

  if (addWindowTimer) {
    clearTimeout(addWindowTimer);
    addWindowTimer = undefined;
  }

  if (!isTagging && !isTaggingScheduled) {
    setCheckoutDisabled(false);
  }
}

/**
 * Fetch cart, find untagged Rise lines with recipient props, tag them,
 * and dispatch a CartUpdateEvent so the cart UI refreshes.
 */
async function runTagging() {
  if (isTagging) return;
  isTagging = true;
  isTaggingScheduled = false;

  try {
    const cart = await fetchCartJson();
    const items = cart.items || [];

    const untaggedRiseLines = items.filter((item) => {
      const props = item.properties || {};
      if (props._isgiftcard === 'true' || props.__isgiftcard === 'true') return false;

      // Rise AI uses capitalized property names: Email, Name, Message
      const hasRecipientProps =
        props.Email || props.email || props.recipient_email || props._recipient_email;
      if (!hasRecipientProps) return false;

      return item.vendor === 'Rise.ai' || item.product_type === 'Gift Cards';
    });

    if (untaggedRiseLines.length === 0) return;

    let anyTagged = false;
    for (const item of untaggedRiseLines) {
      const tagged = await tagLineAsGiftCard(item);
      if (tagged) anyTagged = true;
    }

    if (anyTagged) {
      const updatedCart = await fetchCartJson();
      document.dispatchEvent(new CartUpdateEvent(updatedCart, 'rise-gift-card-tagger', {
        source: 'rise-gift-card-tagger',
        itemCount: updatedCart.item_count,
      }));
    }
  } catch (e) {
    console.error('Error tagging Rise gift card lines', e);
  } finally {
    isTagging = false;
    setCheckoutDisabled(false);
  }
}

// ─── Detect Rise AI's second /cart/add.js ───────────────────────────
//
// Rise AI's gift card flow:
//   1. Theme calls /cart/add.js (first add — the initial product)
//   2. Rise calls its own server, then calls /cart/add.js again
//      (second add — the final line with email/message properties)
//
// We count /cart/add.js calls. When we see a second add within
// ADD_WINDOW_MS of the first, that's Rise's add — we wait a beat
// for the response to settle, then tag.

const ADD_WINDOW_MS = 10000;

/** @type {number} */
let addCount = 0;

/** @type {ReturnType<typeof setTimeout> | undefined} */
let addWindowTimer;

/**
 * Called after every successful /cart/add.js response.
 * Ignores the first add (theme's), triggers tagging on the second (Rise's).
 */
function onCartAddComplete() {
  addCount++;

  // First add — start the window, don't tag yet
  if (addCount === 1) {
    setCheckoutDisabled(true);
    if (addWindowTimer) clearTimeout(addWindowTimer);
    addWindowTimer = setTimeout(() => {
      resetAddWindowState();
    }, ADD_WINDOW_MS);
    return;
  }

  // Second (or later) add within the window — this is Rise's add.
  // Wait for Rise's response handler to finish processing, then tag.
  isTaggingScheduled = true;
  setTimeout(() => runTagging(), 800);

  // Reset for the next gift card add
  resetAddWindowState();
}

/**
 * Check if a URL is a /cart/add endpoint
 * @param {string} url
 * @returns {boolean}
 */
function isCartAddUrl(url) {
  return url.includes('/cart/add');
}

/**
 * Intercept fetch to detect /cart/add.js completions.
 */
const originalFetch = window.fetch;
window.fetch = function (...args) {
  const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
  if (isCartAddUrl(url)) {
    setCheckoutDisabled(true);
  }

  /** @type {Promise<Response>} */
  const result = originalFetch.apply(this, args);

  if (isCartAddUrl(url)) {
    result.then((response) => {
      if (response.ok) onCartAddComplete();
      else if (!isTagging && !isTaggingScheduled) setCheckoutDisabled(false);
    }).catch(() => {});
  }
  return result;
};

/**
 * Intercept XMLHttpRequest to detect /cart/add.js completions
 * from scripts (like Rise AI) that use XHR instead of fetch.
 */
const originalXhrOpen = XMLHttpRequest.prototype.open;
XMLHttpRequest.prototype.open = function (method, url, ...rest) {
  if (typeof url === 'string' && isCartAddUrl(url)) {
    setCheckoutDisabled(true);
    this.addEventListener('load', function () {
      if (this.status >= 200 && this.status < 300) {
        onCartAddComplete();
      } else if (!isTagging && !isTaggingScheduled) {
        setCheckoutDisabled(false);
      }
    });
  }
  return originalXhrOpen.call(this, method, url, ...rest);
};

document.addEventListener('submit', (event) => {
  if (!isCheckoutLocked) return;
  if (!(event.target instanceof HTMLFormElement)) return;
  if (event.target.id !== 'cart-form') return;

  const submitEvent = /** @type {SubmitEvent} */ (event);
  const submitter = submitEvent.submitter;

  if (submitter instanceof HTMLElement) {
    const submitterName = submitter.getAttribute('name');
    if (submitterName && submitterName !== 'checkout') return;
  }

  event.preventDefault();
  event.stopPropagation();
  syncCheckoutButtons();
}, true);

const checkoutButtonObserver = new MutationObserver(() => {
  if (!isCheckoutLocked) return;
  syncCheckoutButtons();
});

if (document.body) {
  checkoutButtonObserver.observe(document.body, { childList: true, subtree: true });
}

/**
 * Safety net: also tag on cart drawer open in case the interception missed it.
 */
const cartDrawer = document.querySelector('cart-drawer-component dialog.cart-drawer__dialog');
if (cartDrawer) {
  const observer = new MutationObserver(() => {
    if (cartDrawer.hasAttribute('open')) {
      runTagging();
    }
  });
  observer.observe(cartDrawer, { attributes: true, attributeFilter: ['open'] });
}
